import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
    Card, Row, Col, Typography, Button, Spin, Empty, Image as AntdImage,
    Modal, Form, Input, message, Slider, Radio, Select, Space, Tag, Checkbox, Tooltip
} from "antd";
import {
    FolderFilled, PlusOutlined, ArrowLeftOutlined, QuestionCircleOutlined,
    DeleteOutlined, EditOutlined
} from "@ant-design/icons";
import {
    batchUpdateImages, createImageFolder, deleteImage, deleteImageFolder, getFolderImages,
    getImageFolders, updateImage, updateImageFolder, uploadImages
} from "@/redux/actions/imageActions";
import ImageSelector from "@/components/ImageSelector";
import Cropper from "react-easy-crop";
import { checkPermission } from "@/utils/permission";
import { isMobile } from "react-device-detect";

const { Text } = Typography;
const { confirm } = Modal;
const { Option } = Select;

const STATUS_LABELS = {
    danglam: "Đang điều trị",
    dahoanthanh: "Đã hoàn thành",
    dadatcoc: "Đã đặt cọc",
    chualam: "Chưa làm",
};

const ImageTab = ({ customer }) => {
    const dispatch = useDispatch();
    const customerId = customer?.id || null;
    const [folders, setFolders] = useState([]);
    const [customerServices, setCustomerServices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [imageLoading, setImageLoading] = useState(false);
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [images, setImages] = useState([]);
    const [contextType, setContextType] = useState(null);
    const [customerServiceId, setCustomerServiceId] = useState(null);
    const [resetKey, setResetKey] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);

    const [isFolderModalVisible, setIsFolderModalVisible] = useState(false);
    const [editingFolder, setEditingFolder] = useState(null);
    const [folderForm] = Form.useForm();
    const [moveModalVisible, setMoveModalVisible] = useState(false);
    const [movingImage, setMovingImage] = useState(null);
    const [moveForm] = Form.useForm();
    const [selectedImageIds, setSelectedImageIds] = useState([]);
    const [assignmentLoading, setAssignmentLoading] = useState(false);

    const [cropModalVisible, setCropModalVisible] = useState(false);
    const [croppingImage, setCroppingImage] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [rotation, setRotation] = useState(0);
    const [guideVisible, setGuideVisible] = useState(false);
    const CROP_ASPECT = 4 / 3;
    const [aspect, setAspect] = useState(CROP_ASPECT);
    const [cropperKey, setCropperKey] = useState(0);

    const allowCreateFolder = checkPermission("images", "create_folder");
    const allowUpdateFolder = checkPermission("images", "update_folder");
    const allowDeleteFolder = checkPermission("images", "delete_folder");
    const allowCreateImage = checkPermission("images", "create_image");
    const allowUpdateImage = checkPermission("images", "update_image");
    const allowDeleteImage = checkPermission("images", "delete_image");

    const getFolderReference = (folder) => folder?.is_virtual
        ? { folder_type_id: folder.id, custom_folder_id: null }
        : { folder_type_id: null, custom_folder_id: folder?.id };

    const getServiceLabel = (service) => {
        const status = STATUS_LABELS[service.status] || service.status || "Chưa cập nhật trạng thái";
        return `${service.name} — ${status}`;
    };

    const loadFolders = useCallback(async () => {
        if (!customerId) return [];
        setLoading(true);
        try {
            const response = await dispatch(getImageFolders(customerId));
            const data = response.data || {};
            const nextFolders = data.folders || [];
            setFolders(nextFolders);
            setCustomerServices(data.customer_services || []);
            return nextFolders;
        } catch (error) {
            setFolders([]);
            setCustomerServices([]);
            return [];
        } finally {
            setLoading(false);
        }
    }, [customerId, dispatch]);

    const loadImages = async (folder = selectedFolder, nextContext = contextType, nextServiceId = customerServiceId) => {
        setSelectedImageIds([]);
        if (!folder || !nextContext || (nextContext === "SERVICE" && !nextServiceId)) {
            setImages([]);
            return;
        }
        setImageLoading(true);
        try {
            const response = await dispatch(getFolderImages(customerId, {
                ...getFolderReference(folder),
                context_type: nextContext,
                customer_service_id: nextContext === "SERVICE" ? nextServiceId : null,
            }));
            setImages(response.data?.images || []);
        } catch (error) {
            setImages([]);
        } finally {
            setImageLoading(false);
        }
    };

    const refreshCurrentFolder = async () => {
        await loadFolders();
        await loadImages();
    };

    useEffect(() => {
        setSelectedFolder(null);
        setImages([]);
        setContextType(null);
        setCustomerServiceId(null);
        loadFolders();
    }, [customerId, loadFolders]);

    const handleOpenFolder = async (folder) => {
        let nextContext = null;
        let nextServiceId = null;
        if (!folder.is_virtual) {
            nextContext = folder.scope_type;
            nextServiceId = folder.customer_service_id || null;
        } else if (folder.scope_mode === "CUSTOMER") {
            nextContext = "CUSTOMER";
        } else if (folder.scope_mode === "BOTH") {
            nextContext = "ALL";
        }
        setSelectedFolder(folder);
        setContextType(nextContext);
        setCustomerServiceId(nextServiceId);
        setSelectedImageIds([]);
        await loadImages(folder, nextContext, nextServiceId);
    };

    const handleContextChange = async (value) => {
        setContextType(value);
        setCustomerServiceId(null);
        if (value === "SERVICE") {
            setImages([]);
            return;
        }
        await loadImages(selectedFolder, value, null);
    };

    const handleServiceChange = async (value) => {
        setCustomerServiceId(value);
        await loadImages(selectedFolder, "SERVICE", value);
    };

    const handleImageChange = async (fileList, changeDetails) => {
        if (changeDetails.action !== "add") return;
        const uploadItems = fileList.map(file => ({
            manual_id: file.manual_id,
            original_name: file.originalFileName,
            src: file.originalImage,
        }));
        try {
            setUploading(true);
            await dispatch(uploadImages(customerId, {
                ...getFolderReference(selectedFolder),
                context_type: contextType,
                customer_service_id: contextType === "SERVICE" ? customerServiceId : null,
                images: uploadItems,
            }));
            await refreshCurrentFolder();
            setResetKey(previous => previous + 1);
        } catch (error) {
            console.error(error);
        } finally {
            setUploading(false);
        }
    };

    const openCreateFolderModal = () => {
        setEditingFolder(null);
        folderForm.resetFields();
        folderForm.setFieldsValue({ scope_type: "CUSTOMER" });
        setIsFolderModalVisible(true);
    };

    const openEditFolderModal = (folder) => {
        setEditingFolder(folder);
        folderForm.setFieldsValue({ name: folder.name, description: folder.description });
        setIsFolderModalVisible(true);
    };

    const handleSaveFolder = async () => {
        try {
            const values = await folderForm.validateFields();
            if (editingFolder) {
                await dispatch(updateImageFolder(editingFolder.id, {
                    name: values.name,
                    description: values.description,
                }));
            } else {
                await dispatch(createImageFolder({
                    customer_id: customerId,
                    name: values.name,
                    description: values.description,
                    scope_type: values.scope_type,
                    customer_service_id: values.scope_type === "SERVICE" ? values.customer_service_id : null,
                }));
            }
            setIsFolderModalVisible(false);
            await loadFolders();
        } catch (error) {
            console.error(error);
        }
    };

    const handleDeleteFolder = (folder) => {
        confirm({
            title: "Xóa thư mục",
            content: "Chỉ thư mục không còn ảnh mới có thể xóa. Bạn có chắc chắn muốn tiếp tục?",
            okText: "Xóa",
            cancelText: "Hủy",
            okButtonProps: { danger: true },
            onOk: async () => {
                await dispatch(deleteImageFolder(folder.id));
                await loadFolders();
            },
        });
    };

    const handleDeleteImage = (image) => {
        confirm({
            title: "Xác nhận",
            content: "Bạn có chắc chắn muốn xoá ảnh này?",
            okText: "Xoá",
            cancelText: "Huỷ",
            okButtonProps: { danger: true },
            onOk: async () => {
                await dispatch(deleteImage(image.id));
                await refreshCurrentFolder();
            },
        });
    };

    const getImageFolder = (image) => folders.find(folder => folder.is_virtual
        ? Number(folder.id) === Number(image.folder_type_id)
        : Number(folder.id) === Number(image.custom_folder_id));

    const setAssignmentForFolder = (folder, preferredContext = "CUSTOMER", preferredServiceId = null) => {
        if (!folder) return;
        if (!folder.is_virtual) {
            moveForm.setFieldsValue({
                context_type: folder.scope_type,
                customer_service_id: folder.scope_type === "SERVICE" ? folder.customer_service_id : null,
            });
            return;
        }

        let nextContext = preferredContext;
        if (folder.scope_mode === "CUSTOMER") nextContext = "CUSTOMER";
        if (folder.scope_mode === "SERVICE") nextContext = "SERVICE";
        if (folder.scope_mode === "BOTH" && !["CUSTOMER", "SERVICE"].includes(nextContext)) nextContext = "CUSTOMER";
        moveForm.setFieldsValue({
            context_type: nextContext,
            customer_service_id: nextContext === "SERVICE" ? preferredServiceId : null,
        });
    };

    const openMoveImageModal = (image) => {
        setMovingImage(image);
        moveForm.resetFields();
        const currentFolder = getImageFolder(image) || selectedFolder;
        moveForm.setFieldsValue({ folder_key: currentFolder?.folder_key });
        setAssignmentForFolder(
            currentFolder,
            image.customer_service_id ? "SERVICE" : "CUSTOMER",
            image.customer_service_id || null
        );
        setMoveModalVisible(true);
    };

    const openBatchMoveModal = () => {
        if (!selectedImageIds.length) return;
        setMovingImage(null);
        moveForm.resetFields();
        moveForm.setFieldsValue({ folder_key: selectedFolder?.folder_key });
        setAssignmentForFolder(
            selectedFolder,
            contextType === "SERVICE" ? "SERVICE" : "CUSTOMER",
            contextType === "SERVICE" ? customerServiceId : null
        );
        setMoveModalVisible(true);
    };

    const handleDestinationFolderChange = (folderKey) => {
        const destination = folders.find(folder => folder.folder_key === folderKey);
        setAssignmentForFolder(destination, moveForm.getFieldValue("context_type"), moveForm.getFieldValue("customer_service_id"));
    };

    const handleAssignmentContextChange = (value) => {
        moveForm.setFieldsValue({ customer_service_id: value === "SERVICE" ? undefined : null });
    };

    const handleMoveImage = async () => {
        try {
            setAssignmentLoading(true);
            const values = await moveForm.validateFields();
            const destination = folders.find(folder => folder.folder_key === values.folder_key);
            const payload = {
                ...getFolderReference(destination),
                context_type: values.context_type,
                customer_service_id: values.context_type === "SERVICE" ? values.customer_service_id : null,
            };
            if (movingImage) {
                await dispatch(updateImage(movingImage.id, payload));
            } else {
                await dispatch(batchUpdateImages(customerId, {
                    ...payload,
                    image_ids: selectedImageIds,
                }));
            }
            setMoveModalVisible(false);
            setSelectedImageIds([]);
            await refreshCurrentFolder();
        } catch (error) {
            console.error(error);
        } finally {
            setAssignmentLoading(false);
        }
    };

    const toggleImageSelection = (imageId, checked) => {
        setSelectedImageIds(previous => checked
            ? Array.from(new Set([...previous, imageId]))
            : previous.filter(id => id !== imageId));
    };

    const allVisibleImagesSelected = images.length > 0 && selectedImageIds.length === images.length;
    const toggleAllVisibleImages = (checked) => {
        setSelectedImageIds(checked ? images.map(image => image.id) : []);
    };

    const showCropModal = (image) => {
        setCroppingImage(image);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setRotation(0);
        setCropModalVisible(true);
        setAspect(CROP_ASPECT);
        setCropperKey(previous => previous + 1);
    };

    const onCropComplete = (_, croppedAreaPx) => setCroppedAreaPixels(croppedAreaPx);

    const getCroppedImg = async (imageSrc, cropPixels, imageRotation = 0, imageZoom = 1) => {
        const cleanSrc = imageSrc.split("?")[0];
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.referrerPolicy = "no-referrer";
        image.src = `${cleanSrc}?v=${Date.now()}`;
        await image.decode();
        const effectiveCrop = cropPixels?.width && cropPixels?.height ? cropPixels : {
            x: 0, y: 0, width: image.width, height: image.height,
        };
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        const radians = (imageRotation * Math.PI) / 180;
        const width = image.width * imageZoom;
        const height = image.height * imageZoom;
        canvas.width = effectiveCrop.width;
        canvas.height = effectiveCrop.height;
        context.translate(-effectiveCrop.x, -effectiveCrop.y);
        context.translate(width / 2, height / 2);
        context.rotate(radians);
        context.drawImage(image, -image.width / 2, -image.height / 2, image.width, image.height);
        return canvas.toDataURL("image/jpeg", 0.92);
    };

    const handleSaveCropped = async () => {
        try {
            setSaveLoading(true);
            const croppedBase64 = await getCroppedImg(
                croppingImage.file_url_original,
                croppedAreaPixels,
                rotation,
                zoom
            );
            await dispatch(uploadImages(customerId, {
                ...getFolderReference(selectedFolder),
                context_type: contextType,
                customer_service_id: contextType === "SERVICE" ? customerServiceId : null,
                images: [{
                    manual_id: Date.now(),
                    original_name: `${(croppingImage.original_name || "cropped-image").replace(/\.[^/.]+$/, "")}-cropped.jpg`,
                    src: croppedBase64,
                }],
            }));
            await dispatch(deleteImage(croppingImage.id));
            await refreshCurrentFolder();
        } catch (error) {
            console.error(error);
            message.error("Lưu ảnh thất bại");
        } finally {
            setSaveLoading(false);
            setCropModalVisible(false);
            setRotation(0);
            setZoom(1);
            setAspect(CROP_ASPECT);
        }
    };

    useEffect(() => {
        setCrop({ x: 0, y: 0 });
    }, [aspect]);

    const canUpload = allowCreateImage && selectedFolder
        && ["CUSTOMER", "SERVICE"].includes(contextType)
        && (contextType !== "SERVICE" || customerServiceId);

    return (
        <div>
            <div style={{ marginBottom: 30 }} className="mt-4">
                <Row gutter={8} justify="space-between" align="middle">
                    <Col>
                        {selectedFolder ? (
                            <Button icon={<ArrowLeftOutlined />} onClick={() => {
                                setSelectedFolder(null);
                                setImages([]);
                                setContextType(null);
                                setCustomerServiceId(null);
                                setSelectedImageIds([]);
                            }}>
                                Quay lại thư mục
                            </Button>
                        ) : (
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={openCreateFolderModal}
                                disabled={!allowCreateFolder}
                            >
                                Tạo thư mục mới
                            </Button>
                        )}
                    </Col>
                    <Col>
                        <Button type="link" icon={<QuestionCircleOutlined />} onClick={() => setGuideVisible(true)}>
                            Xem hướng dẫn chụp ảnh
                        </Button>
                    </Col>
                </Row>
            </div>

            {!selectedFolder ? (
                loading ? <div style={{ textAlign: "center", minHeight: 100 }}><Spin /></div> : (
                    folders.length ? (
                        <Row gutter={[24, 24]}>
                            {folders.map(folder => (
                                <Col key={folder.folder_key}>
                                    <Card
                                        hoverable
                                        onClick={() => handleOpenFolder(folder)}
                                        style={{ width: 130, textAlign: "center", border: "none", boxShadow: "none" }}
                                        bodyStyle={{ padding: 0 }}
                                    >
                                        <FolderFilled style={{ fontSize: 80, color: "#f4a300" }} />
                                        <div><Text style={{ fontSize: 12 }}>{folder.name} ({folder.count})</Text></div>
                                        {!folder.is_virtual && (
                                            <Space style={{ marginTop: 8 }}>
                                                <Button size="small" disabled={!allowUpdateFolder} onClick={event => {
                                                    event.stopPropagation();
                                                    openEditFolderModal(folder);
                                                }}>Sửa</Button>
                                                <Button
                                                    size="small"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    disabled={!allowDeleteFolder}
                                                    onClick={event => {
                                                        event.stopPropagation();
                                                        handleDeleteFolder(folder);
                                                    }}
                                                />
                                            </Space>
                                        )}
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    ) : <Empty description="Chưa có thư mục ảnh" />
                )
            ) : (
                <div>
                    <Text strong style={{ display: "block", marginBottom: 16 }}>Thư mục: {selectedFolder.name}</Text>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            marginBottom: 20,
                            flexWrap: "wrap",
                        }}
                    >
                        <Text strong style={{ flexShrink: 0 }}>
                            Ảnh áp dụng cho
                        </Text>

                        {selectedFolder.is_virtual ? (
                            <Select
                                value={contextType}
                                placeholder="Chọn phạm vi"
                                style={{ width: 220 }}
                                onChange={handleContextChange}
                            >
                                {selectedFolder.scope_mode === "BOTH" && (
                                    <Option value="ALL">Xem tất cả ảnh</Option>
                                )}

                                {["CUSTOMER", "BOTH"].includes(selectedFolder.scope_mode) && (
                                    <Option value="CUSTOMER">
                                        Ảnh chung của khách hàng
                                    </Option>
                                )}

                                {["SERVICE", "BOTH"].includes(selectedFolder.scope_mode) && (
                                    <Option value="SERVICE">
                                        Dịch vụ chốt
                                    </Option>
                                )}
                            </Select>
                        ) : (
                            <Input
                                value={
                                    selectedFolder.scope_type === "CUSTOMER"
                                        ? "Ảnh chung của khách hàng"
                                        : selectedFolder.customer_service_name || "Dịch vụ chốt"
                                }
                                disabled
                                style={{ width: 220 }}
                            />
                        )}

                        {contextType === "SERVICE" && selectedFolder.is_virtual && (
                            <Select
                                value={customerServiceId}
                                placeholder="Chọn dịch vụ chốt"
                                showSearch
                                optionFilterProp="children"
                                style={{ width: 320 }}
                                onChange={handleServiceChange}
                            >
                                {customerServices.map(service => (
                                    <Option key={service.id} value={service.id}>
                                        {getServiceLabel(service)}
                                    </Option>
                                ))}
                            </Select>
                        )}
                    </div>

                    {canUpload && (
                        <ImageSelector
                            onChangeData={handleImageChange}
                            multiple
                            maxCount={50}
                            uploadText="Chọn ảnh để tải lên"
                            textSize={15}
                            width={100}
                            height={100}
                            resetSignal={resetKey}
                        />
                    )}

                    <div style={{ marginTop: 20 }}>
                        {allowUpdateImage && images.length > 0 && (
                            <div style={{
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                gap: 12, flexWrap: "wrap", padding: "10px 12px", marginBottom: 12,
                                background: selectedImageIds.length ? "#e6f7ff" : "#fafafa",
                                border: "1px solid #d9d9d9", borderRadius: 4,
                            }}>
                                <Checkbox
                                    checked={allVisibleImagesSelected}
                                    indeterminate={selectedImageIds.length > 0 && !allVisibleImagesSelected}
                                    onChange={event => toggleAllVisibleImages(event.target.checked)}
                                >
                                    {selectedImageIds.length
                                        ? `Đã chọn ${selectedImageIds.length} ảnh`
                                        : "Chọn tất cả ảnh đang hiển thị"}
                                </Checkbox>
                                {selectedImageIds.length > 0 && (
                                    <Space>
                                        <Button size="small" onClick={() => setSelectedImageIds([])}>Bỏ chọn</Button>
                                        <Button size="small" type="primary" icon={<EditOutlined />} onClick={openBatchMoveModal}>
                                            Đổi thư mục / áp dụng cho
                                        </Button>
                                    </Space>
                                )}
                            </div>
                        )}
                        <Spin spinning={uploading || imageLoading} tip="Đang xử lý ảnh...">
                            {images.length ? (
                                <AntdImage.PreviewGroup>
                                    <Row gutter={[16, 16]} style={{ width: "100%", margin: 0 }}>
                                        {images.map(image => (
                                            <Col key={image.id} xs={24} sm={12} md={8} lg={6} xl={6}>
                                                <div style={{ width: "100%" }}>
                                                    <div style={{
                                                        position: "relative", width: "100%", aspectRatio: "4 / 3",
                                                        border: "1px solid #f0f0f0", borderRadius: 4, overflow: "hidden",
                                                        background: "#f9f9f9d0"
                                                    }}>
                                                        <AntdImage
                                                            src={image.file_url_thumb}
                                                            preview={{ src: `${image.file_url_original}?v=${Date.now()}` }}
                                                            alt={image.original_name || image.file_name}
                                                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                        />
                                                        {allowUpdateImage && (
                                                            <span style={{
                                                                position: "absolute", top: 8, left: 8, zIndex: 3,
                                                                padding: "3px 6px", background: "rgba(255,255,255,0.92)", borderRadius: 4,
                                                            }} onClick={event => event.stopPropagation()}>
                                                                <Checkbox
                                                                    checked={selectedImageIds.includes(image.id)}
                                                                    onChange={event => toggleImageSelection(image.id, event.target.checked)}
                                                                />
                                                            </span>
                                                        )}
                                                        <Button
                                                            size="small"
                                                            type="primary"
                                                            style={{ position: "absolute", top: 8, left: 48, zIndex: 2 }}
                                                            disabled={!allowCreateImage || !allowDeleteImage || contextType === "ALL"}
                                                            onClick={event => { event.stopPropagation(); showCropModal(image); }}
                                                        >Cắt ảnh</Button>
                                                        <Button
                                                            size="small"
                                                            icon={<EditOutlined />}
                                                            style={{ position: "absolute", bottom: 8, left: 8, zIndex: 2 }}
                                                            disabled={!allowUpdateImage}
                                                            onClick={event => { event.stopPropagation(); openMoveImageModal(image); }}
                                                        >Sửa</Button>
                                                        <Button
                                                            danger
                                                            size="small"
                                                            style={{ position: "absolute", top: 8, right: 8, zIndex: 2 }}
                                                            disabled={!allowDeleteImage}
                                                            onClick={event => { event.stopPropagation(); handleDeleteImage(image); }}
                                                        >Xoá</Button>
                                                    </div>
                                                    <div style={{
                                                        minHeight: 24, marginTop: 6, textAlign: "center",
                                                        width: "100%", overflow: "hidden",
                                                    }}>
                                                        {image.customer_service_id && image.customer_service_name && (
                                                            <Tooltip title={image.customer_service_name}>
                                                                <Tag style={{
                                                                    maxWidth: "100%", marginRight: 0,
                                                                    color: "#595959", background: "#fafafa", borderColor: "#d9d9d9",
                                                                }}>
                                                                    <span style={{
                                                                        display: "block", maxWidth: "100%",
                                                                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                                    }}>
                                                                        {image.customer_service_name}
                                                                    </span>
                                                                </Tag>
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                </AntdImage.PreviewGroup>
                            ) : <Empty description={contextType === "SERVICE" && !customerServiceId
                                ? "Vui lòng chọn dịch vụ chốt"
                                : "Chưa có ảnh trong phạm vi này"} />}
                        </Spin>
                    </div>
                </div>
            )}

            <Modal
                open={isFolderModalVisible}
                title={editingFolder ? "Sửa thư mục" : "Tạo thư mục mới"}
                onCancel={() => setIsFolderModalVisible(false)}
                onOk={handleSaveFolder}
                okText={editingFolder ? "Lưu" : "Tạo"}
                cancelText="Hủy"
                maskClosable={false}
            >
                <Form layout="vertical" form={folderForm}>
                    <Form.Item label="Tên thư mục" name="name" rules={[
                        { required: true, message: "Vui lòng nhập tên thư mục" },
                        { max: 255, message: "Tên thư mục không được vượt quá 255 ký tự" },
                    ]}>
                        <Input />
                    </Form.Item>
                    {!editingFolder && (
                        <>
                            <Form.Item label="Phạm vi" name="scope_type" rules={[{ required: true }]}>
                                <Radio.Group>
                                    <Radio value="CUSTOMER">Ảnh chung khách hàng</Radio>
                                    <Radio value="SERVICE">Dịch vụ chốt</Radio>
                                </Radio.Group>
                            </Form.Item>
                            <Form.Item noStyle shouldUpdate={(previous, current) => previous.scope_type !== current.scope_type}>
                                {({ getFieldValue }) => getFieldValue("scope_type") === "SERVICE" ? (
                                    <Form.Item
                                        label="Dịch vụ chốt"
                                        name="customer_service_id"
                                        rules={[{ required: true, message: "Vui lòng chọn dịch vụ chốt" }]}
                                    >
                                        <Select showSearch optionFilterProp="children" placeholder="Chọn dịch vụ chốt">
                                            {customerServices.map(service => (
                                                <Option key={service.id} value={service.id}>{getServiceLabel(service)}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                ) : null}
                            </Form.Item>
                        </>
                    )}
                    <Form.Item label="Mô tả" name="description"><Input.TextArea rows={3} /></Form.Item>
                </Form>
            </Modal>

            <Modal
                open={moveModalVisible}
                title={movingImage ? "Sửa thư mục và phạm vi ảnh" : `Cập nhật ${selectedImageIds.length} ảnh đã chọn`}
                onCancel={() => { setMoveModalVisible(false); setMovingImage(null); }}
                onOk={handleMoveImage}
                okText="Lưu"
                cancelText="Hủy"
                maskClosable={false}
                confirmLoading={assignmentLoading}
            >
                <Form layout="vertical" form={moveForm}>
                    <Form.Item
                        label="Thư mục mới"
                        name="folder_key"
                        rules={[{ required: true, message: "Vui lòng chọn thư mục" }]}
                    >
                        <Select
                            showSearch
                            optionFilterProp="children"
                            placeholder="Chọn thư mục"
                            onChange={handleDestinationFolderChange}
                        >
                            {folders.map(folder => (
                                <Option key={folder.folder_key} value={folder.folder_key}>
                                    {folder.name}{folder.is_virtual ? " — Hệ thống" : " — Tùy chỉnh"}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(previous, current) => previous.folder_key !== current.folder_key
                        || previous.context_type !== current.context_type}>
                        {({ getFieldValue }) => {
                            const destination = folders.find(folder => folder.folder_key === getFieldValue("folder_key"));
                            if (!destination) return null;
                            const isFixedCustomFolder = !destination.is_virtual;
                            const allowedContexts = destination.is_virtual
                                ? destination.scope_mode === "BOTH" ? ["CUSTOMER", "SERVICE"] : [destination.scope_mode]
                                : [destination.scope_type];
                            return (
                                <>
                                    <Form.Item
                                        label="Ảnh áp dụng cho"
                                        name="context_type"
                                        rules={[{ required: true, message: "Vui lòng chọn phạm vi ảnh" }]}
                                    >
                                        <Radio.Group disabled={isFixedCustomFolder} onChange={event => handleAssignmentContextChange(event.target.value)}>
                                            {allowedContexts.includes("CUSTOMER") && (
                                                <Radio value="CUSTOMER">Ảnh chung của khách hàng</Radio>
                                            )}
                                            {allowedContexts.includes("SERVICE") && (
                                                <Radio value="SERVICE">Dịch vụ chốt</Radio>
                                            )}
                                        </Radio.Group>
                                    </Form.Item>
                                    {getFieldValue("context_type") === "SERVICE" && (
                                        <Form.Item
                                            label="Dịch vụ chốt"
                                            name="customer_service_id"
                                            rules={[{ required: true, message: "Vui lòng chọn dịch vụ chốt" }]}
                                        >
                                            <Select
                                                showSearch
                                                optionFilterProp="children"
                                                placeholder="Chọn dịch vụ chốt"
                                                disabled={isFixedCustomFolder}
                                            >
                                                {customerServices.map(service => (
                                                    <Option key={service.id} value={service.id}>{getServiceLabel(service)}</Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    )}
                                </>
                            );
                        }}
                    </Form.Item>
                    <Text type="secondary">Thao tác chỉ cập nhật thư mục/phạm vi trong dữ liệu, không di chuyển file ảnh trên storage.</Text>
                </Form>
            </Modal>

            <Modal
                open={cropModalVisible}
                title="Cắt ảnh"
                onOk={handleSaveCropped}
                onCancel={() => { setCropModalVisible(false); setAspect(CROP_ASPECT); }}
                okText="Lưu ảnh mới"
                cancelText="Huỷ"
                width={800}
                maskClosable={false}
                confirmLoading={saveLoading}
                style={{ top: 10 }}
            >
                <Row gutter={16}>
                    <Col span={24}>
                        <div style={{ width: "100%", height: 400, position: "relative" }}>
                            <Cropper
                                key={cropperKey}
                                image={croppingImage?.file_url_original}
                                crop={crop}
                                zoom={zoom}
                                aspect={aspect}
                                rotation={rotation}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={onCropComplete}
                                onRotationChange={setRotation}
                            />
                        </div>
                        <div style={{ marginTop: 20 }}>
                            <div style={{ marginBottom: 15 }}>
                                <Text>Chọn tỷ lệ khung cắt:</Text>
                                <Radio.Group value={aspect} onChange={event => setAspect(event.target.value)} style={{ marginLeft: 12 }}>
                                    <Radio.Button value={1}>1:1</Radio.Button>
                                    <Radio.Button value={4 / 3}>4:3</Radio.Button>
                                    <Radio.Button value={3 / 4}>3:4</Radio.Button>
                                    <Radio.Button value={16 / 9}>16:9</Radio.Button>
                                    <Radio.Button value={9 / 16}>9:16</Radio.Button>
                                    <Radio.Button value={2 / 3}>2:3</Radio.Button>
                                    <Radio.Button value={5 / 4}>5:4</Radio.Button>
                                </Radio.Group>
                            </div>
                            <div style={{ marginBottom: 10 }}><Text>Zoom:</Text><Slider min={1} max={3} step={0.1} value={zoom} onChange={setZoom} /></div>
                            <div><Text>Xoay:</Text><Slider min={0} max={360} step={1} value={rotation} onChange={setRotation} /></div>
                            <Button onClick={() => { setCrop({ x: 0, y: 0 }); setZoom(1); setRotation(0); }} style={{ marginTop: 10 }}>Reset</Button>
                        </div>
                    </Col>
                </Row>
            </Modal>

            <Modal
                open={guideVisible}
                title="Hướng dẫn chụp ảnh đúng cách"
                onCancel={() => setGuideVisible(false)}
                footer={null}
                width={isMobile ? "100%" : 900}
            >
                <p>Dưới đây là hướng dẫn cách chụp ảnh hồ sơ bệnh nhân đúng chuẩn:</p>
                <AntdImage.PreviewGroup>
                    <Row gutter={[16, 16]}>
                        <Col span={24}><AntdImage src="/guide-images/goc-chup-trong.jpg" style={{ width: "100%", borderRadius: 8 }} preview={false} /></Col>
                        <Col span={24}><AntdImage src="/guide-images/goc-chup-ngoai.jpg" style={{ width: "100%", borderRadius: 8 }} preview={false} /></Col>
                    </Row>
                </AntdImage.PreviewGroup>
            </Modal>
        </div>
    );
};

export default ImageTab;
